import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button } from "@/common/components/ui/button";
import {
  ArrowLeft,
  Save,
  Loader2,
  PlayCircle,
  CheckCircle,
} from "lucide-react";
import { Card, CardContent } from "@/common/components/ui/card";
import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";
import { message } from "antd";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/common/components/ui/select";
import finEaseHomeNavigationRoutes from "@/projects/FinEaseHomeAndAdmin/utils/navigationRoutes";
import ScreenerService from "@/services/FinEaseHomeAndAdmin/APIs/ScrennerService";
import { QueryCategory } from "@/utils/types/Screener/Screener";

interface QueryMasterUserPayload {
  title: string;
  sub_title: string;
  category: string;
  query: string;
  user: string;
}

// Removed staticQueries

const AddEditQueryMasterUser = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const isEdit = !!id;
  const isViewMode = location.pathname.includes("/view/");

  const [formData, setFormData] = useState<QueryMasterUserPayload>({
    title: "",
    sub_title: "",
    category: "",
    query: "",
    user: "System Admin", // default if backend sets it
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [categories, setCategories] = useState<QueryCategory[]>([]);

  const fetchQueryDetails = async (queryId: string) => {
    setIsLoading(true);
    try {
      const response = await ScreenerService.getUserQueryById(queryId);
      if (response && response.success !== false && response.data) {
        setFormData({
          title: response.data.title ?? "",
          sub_title: response.data.sub_title ?? "",
          category: response.data.category ?? "",
          query: response.data.query ?? "",
          user: response.data.user ?? "System Admin",
        });
        // Assuming fetched query is valid and verified
        setIsVerified(true);
      } else {
        message.warning("Failed to fetch user query details");
      }
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.message ??
          "Something went wrong while fetching user query details."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const response = await ScreenerService.getQueryCategories();
      if (response && response.success !== false) {
        setFormData(prev => ({
          ...prev,
          category: response.data.results[0].title,
        }));
        setCategories(response.data.results ?? []);
      } else {
        message.warning("Failed to fetch categories");
      }
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.message ??
          "Something went wrong while fetching categories."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if ((isEdit || isViewMode) && id) {
      fetchQueryDetails(id);
    }
    fetchCategories();
  }, [id, isEdit, isViewMode]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // If query is modified, set to unverified
    if (name === "query") {
      setIsVerified(false);
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleVerifyQuery = () => {
    if (!formData.query) {
      return;
    }
    setIsVerifying(true);
    setTimeout(() => {
      setIsVerifying(false);
      setIsVerified(true);
    }, 800);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isViewMode) {
      navigate(finEaseHomeNavigationRoutes.QUERY_MASTER_USER);
      return;
    }

    if (!isVerified) {
      message.error("Please verify the query first before saving.");
      return;
    }

    setIsLoading(true);
    try {
      if (isEdit && id) {
        const response: any = await ScreenerService.updateUserQueryById(id, {
          title: formData.title,
          sub_title: formData.sub_title,
          category: formData.category,
          query: formData.query,
        });

        if (response && response.success !== false) {
          message.success(
            response.message ?? "User Query updated successfully!"
          );
          navigate(finEaseHomeNavigationRoutes.QUERY_MASTER_USER);
        } else {
          message.warning(response.message ?? "Failed to update User Query");
        }
      } else {
        const response: any = await ScreenerService.saveUserQuery({
          title: formData.title,
          sub_title: formData.sub_title,
          category: formData.category, // assuming string UUID category
          query: formData.query,
        });

        if (response && response.success !== false) {
          message.success(response.message ?? "User Query saved successfully!");
          navigate(finEaseHomeNavigationRoutes.QUERY_MASTER_USER);
        } else {
          message.warning(response.message ?? "Failed to save User Query");
        }
      }
    } catch (error: any) {
      console.error(error);
      const errorData = error?.response?.data?.error ?? error?.response?.data;

      if (errorData?.fields && typeof errorData.fields === "object") {
        Object.entries(errorData.fields).forEach(([field, messages]) => {
          const msgs = Array.isArray(messages)
            ? messages.join(", ")
            : String(messages);
          const formattedField = field
            .replace(/_/g, " ")
            .replace(/\b\w/g, c => c.toUpperCase());
          message.error(`${formattedField}: ${msgs}`);
        });
      } else {
        message.error(
          errorData?.message ?? "An error occurred while saving the User Query."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50/50 font-sans text-sm">
      <form onSubmit={handleSubmit} className="flex h-full flex-col">
        <div className="sticky top-0 z-30 border-b border-slate-200 bg-white">
          <div className="mx-auto w-full max-w-5xl">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-5">
                <Button
                  variant="outline"
                  size="icon"
                  type="button"
                  onClick={() =>
                    navigate(finEaseHomeNavigationRoutes.QUERY_MASTER_USER)
                  }
                  className="h-10 w-10 border-slate-200 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50"
                >
                  <ArrowLeft className="h-5 w-5 text-slate-600" />
                </Button>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                    {isViewMode
                      ? `View User Query`
                      : isEdit
                        ? `Edit User Query`
                        : "Create User Query"}
                  </h1>
                  <p className="mt-0.5 text-sm font-medium text-slate-500">
                    Configure Screener User Query details
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {isViewMode ? (
                  <Button
                    type="button"
                    variant="default"
                    onClick={() =>
                      navigate(finEaseHomeNavigationRoutes.QUERY_MASTER_USER)
                    }
                    className="h-10 bg-primary px-5 text-sm font-semibold text-white"
                  >
                    Back to List
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={isLoading || !isVerified}
                    className="h-11 gap-2.5 bg-primary px-8 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5 hover:bg-primary/95"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    {isEdit ? "Save Changes" : "Create Query"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex w-full flex-1 overflow-auto bg-slate-50/50">
          <div className="mx-auto w-full max-w-5xl flex-1 p-6">
            <Card className="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
              <CardContent className="space-y-8 p-8">
                <div>
                  <h3 className="mb-6 border-b pb-3 text-lg font-bold text-primary">
                    User Query Information
                  </h3>
                  <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                    <div className="space-y-3">
                      <Label
                        htmlFor="category"
                        className="block text-sm font-semibold text-slate-700"
                      >
                        Category
                      </Label>
                      <Select
                        value={formData.category}
                        onValueChange={val =>
                          handleSelectChange("category", val)
                        }
                        disabled={isViewMode}
                      >
                        <SelectTrigger
                          id="category"
                          className="h-11 w-full border-slate-200 bg-slate-50/50 text-left transition-colors focus:bg-white"
                        >
                          <SelectValue placeholder="Select Category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(category => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-3">
                      <Label
                        htmlFor="title"
                        className="text-sm font-semibold text-slate-700"
                      >
                        Title
                      </Label>
                      <Input
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="e.g. Top IT Stocks"
                        disabled={isViewMode}
                        className="h-11 border-slate-200 bg-slate-50/50 transition-colors placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:bg-white"
                        required
                      />
                    </div>
                    <div className="space-y-3">
                      <Label
                        htmlFor="sub_title"
                        className="text-sm font-semibold text-slate-700"
                      >
                        Sub Title
                      </Label>
                      <Input
                        id="sub_title"
                        name="sub_title"
                        value={formData.sub_title}
                        onChange={handleInputChange}
                        placeholder="e.g. High growth IT sector"
                        disabled={isViewMode}
                        className="h-11 border-slate-200 bg-slate-50/50 transition-colors placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:bg-white"
                        required
                      />
                    </div>

                    <div className="col-span-1 space-y-3 md:col-span-2">
                      <div className="flex items-center justify-between">
                        <Label
                          htmlFor="query"
                          className="text-sm font-semibold text-slate-700"
                        >
                          Query
                        </Label>
                        {!isViewMode && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className={`h-8 gap-1.5 ${isVerified ? "border-green-200 bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700" : ""}`}
                            onClick={handleVerifyQuery}
                            disabled={
                              !formData.query || isVerifying || isVerified
                            }
                          >
                            {isVerifying ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : isVerified ? (
                              <CheckCircle className="h-3.5 w-3.5" />
                            ) : (
                              <PlayCircle className="h-3.5 w-3.5" />
                            )}
                            {isVerifying
                              ? "Verifying..."
                              : isVerified
                                ? "Verified"
                                : "Verify Query"}
                          </Button>
                        )}
                      </div>
                      <textarea
                        id="query"
                        name="query"
                        value={formData.query}
                        onChange={handleInputChange as any}
                        placeholder="Write query logic here..."
                        disabled={isViewMode}
                        rows={4}
                        className="w-full rounded-xl border border-slate-200 bg-slate-50/50 p-3 text-sm transition-colors placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#66A0CD]/20"
                        required
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddEditQueryMasterUser;
